/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* eslint-disable no-duplicate-imports */
import { addThrottledEvent, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IGridSettings, IImageSettings, isEmptyOrNullObject } from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';
import { IMenuItemData, ICountrySelectorData } from './country-selector.data';
import { ICmsNavItemsData, ICountrySelectorProps, ISubMenusData } from './country-selector.props.autogenerated';

export interface ICountrySelectorViewProps extends ICountrySelectorProps<ICountrySelectorData> {
    className: string;
    menuItemData: IMenuItemData[];
    Navigation: IModuleProps;
    MenuList: INodeProps;
    ListItem: INodeProps;
    Link: INodeProps;
    Button: INodeProps;
    Span: INodeProps;
    ImageContainer: INodeProps;
    MobileDescriptionContainer: INodeProps;
    MobileBackButton: INodeProps;
    MobileDescriptionLabel: INodeProps;
    imageSettings: IImageSettings;
    gridSettings: IGridSettings;
    isMobileView: boolean;
    DivContainer?: INodeProps;
    ImageDivContainer?: INodeProps;
    countrySelectorIntroText: string;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
    isMobile: boolean;
}

/**
 *
 * CountrySelector component
 * @extends {React.PureComponent<ICountrySelectorProps<ICountrySelectorData>>}
 */
class CountrySelector extends React.PureComponent<ICountrySelectorProps<ICountrySelectorData>, INavigationMenuState> {
    private static CMSItemIdInit: number = 1000;
    private resizeThrottledEventHandler?: (event: Event) => void;
    private menuItemList: IMenuItemData[];
    private isCMS: boolean = true;
    @observable
    private viewport: GridSize;
    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: ICountrySelectorProps<ICountrySelectorData>) {
        super(props);
        this._updateViewport = this._updateViewport.bind(this);
        this.viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this.state = { isMobile: this.isMobile };
        this.menuItemList = [];
    }

    @computed get isMobile(): boolean {
        return this.viewport === 'xs' || this.viewport === 'sm';
    }

    public componentDidMount(): void {
        this.resizeThrottledEventHandler = window && addThrottledEvent(window, 'resize', this._updateViewport as EventListener);
        this._updateViewport();
    }

    public componentWillUnmount(): void {
        window && window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
    }

    public render(): JSX.Element | null {
        this.menuItemList = this._mapData();
        if (isEmptyOrNullObject(this.props.data)) {
            this.props.telemetry.error('Navigatiomenu returned null');
            return null;
        }

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=224&h=300&m=6`, w: 0, h: 0 },
                xl: { q: `w=224&h=300&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };

        const viewProps = {
            ...this.props,
            ...this.state,
            imageSettings: defaultImageSettings,
            gridSettings: this.props.context.request.gridSettings,
            Navigation: {
                moduleProps: this.props,
                className: classnames(
                    'ms-nav menu--country-selector',
                    this.props.config.className,
                    this.state.isMobile ? 'mobile-vp' : 'desktop-vp'
                ),
                tag: 'nav',
                role: 'navigation',
                'aria-label': this.props.config.countrySelectorAriaLabel
            },
            MenuList: {
                className: 'ms-nav__list',
                tag: 'ul'
            },
            ListItem: {
                className: 'ms-nav__list__item',
                tag: 'li'
            },
            Link: {
                tag: 'a',
                className: 'ms-nav__list__item__link'
            },
            DivContainer: {
                tag: 'div',
                className: 'ms-nav__feature'
            },
            ImageDivContainer: {
                tag: 'div',
                className: 'category-image',
                role: 'tabpanel'
            },
            Button: {
                className: 'ms-nav__list__item__button',
                tag: 'button',
                onClick: this.props
            },
            ImageContainer: {
                className: 'ms-nav__list__item__image'
            },
            Span: {
                className: 'ms-nav__list__item__span',
                tag: 'span'
            },
            MobileDescriptionContainer: {
                className: 'ms-nav__list__mobile__container'
            },
            MobileBackButton: {
                className: 'ms-nav__list__mobile__container__button',
                tag: 'button',
                onClick: this.props
            },
            MobileDescriptionLabel: {
                className: 'ms-nav__list__mobile__container__span',
                tag: 'span'
            },
            isMobileView: this.state.isMobile,
            menuItemData: this.menuItemList,
            className: classnames('ms-nav', this.props.config.className)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _mapData(): IMenuItemData[] {
        const { config } = this.props;
        const mergedData: IMenuItemData[] = [];
        if (this.isCMS && config.cmsNavItems && config.cmsNavItems.length > 0) {
            config.cmsNavItems.map((item: ICmsNavItemsData) => mergedData.push(this._constructMenuItemData(item)));
        }
        return mergedData;
    }

    // tslint:disable-next-line: cyclomatic-complexity
    private _constructMenuItemData(cmsItem?: ICmsNavItemsData): IMenuItemData {
        return {
            id: CountrySelector.CMSItemIdInit++,
            linkText: (cmsItem && cmsItem.linkText) || '',
            linkURL: (cmsItem && cmsItem.linkUrl && cmsItem.linkUrl.destinationUrl) || '',
            imageDestinationURL: (cmsItem && cmsItem.imageLink && cmsItem.imageLink.destinationUrl) || '',
            imageSource: (cmsItem && cmsItem.image && cmsItem.image.src) || '',
            subMenu: this._getCMSNestedChildren(cmsItem)
        } as IMenuItemData;
    }

    private _getCMSNestedChildren(category?: ICmsNavItemsData): IMenuItemData[] {
        const nestedChildren: IMenuItemData[] = [];

        if (!category || !category.subMenus || !category.subMenus.length) {
            return [];
        }

        category.subMenus.map((item: ISubMenusData, idx: number) => {
            nestedChildren.push(this._constructMenuItemData(item));
        });
        return nestedChildren;
    }

    private _updateViewport(): void {
        this.viewport = this._getViewport();
        this.setState({ isMobile: this.isMobile });
    }

    private _getViewport(): GridSize {
        const { context } = this.props;
        // always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }
        if (typeof window !== undefined && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                } else {
                    return 'xl';
                }
            }
        }
        return 'xs';
    }
}

export default CountrySelector;
